"use client";

import { useEffect, useId, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

interface CommunityFilterDropdownProps {
  label: string;
  active?: boolean;
  children: React.ReactNode;
  align?: "left" | "right";
}

function getFocusableItems(panel: HTMLDivElement | null): HTMLElement[] {
  if (!panel) return [];
  return Array.from(
    panel.querySelectorAll<HTMLElement>(
      'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
    ),
  );
}

export function CommunityFilterDropdown({
  label,
  active,
  children,
  align = "right",
}: CommunityFilterDropdownProps) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    // Focus the first item when the panel opens.
    const firstItem = getFocusableItems(panelRef.current)[0];
    firstItem?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
        return;
      }

      const items = getFocusableItems(panelRef.current);
      const currentIndex = items.findIndex(
        (item) => item === document.activeElement,
      );

      if (event.key === "ArrowDown") {
        event.preventDefault();
        const nextIndex =
          currentIndex < items.length - 1 ? currentIndex + 1 : 0;
        items[nextIndex]?.focus();
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        const prevIndex =
          currentIndex > 0 ? currentIndex - 1 : items.length - 1;
        items[prevIndex]?.focus();
      } else if (event.key === "Home") {
        event.preventDefault();
        items[0]?.focus();
      } else if (event.key === "End") {
        event.preventDefault();
        items[items.length - 1]?.focus();
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target as Node) &&
        !triggerRef.current?.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  return (
    <div className="relative w-full sm:w-auto">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={panelId}
        className={cn(
          "inline-flex w-full min-w-0 items-center justify-between gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2 sm:w-auto sm:py-1.5",
          active
            ? "bg-primary-600 text-white border-primary-600 hover:bg-primary-700"
            : "bg-panel text-muted border-line hover:border-line-strong hover:bg-page",
        )}
      >
        <span className="truncate">{label}</span>
        <ChevronDown
          className={cn(
            "w-4 h-4 transition-transform",
            open && "-rotate-180",
          )}
        />
      </button>

      {open && (
        <div
          ref={panelRef}
          id={panelId}
          role="listbox"
          aria-label={label}
          onClick={(event) => {
            if ((event.target as HTMLElement).closest('[role="option"]')) {
              setOpen(false);
              triggerRef.current?.focus();
            }
          }}
          className={cn(
            "absolute z-40 mt-2 w-[min(20rem,calc(100vw-2rem))] rounded-xl border border-line bg-panel p-3 shadow-lg",
            align === "left" ? "left-0" : "right-0",
          )}
        >
          {children}
        </div>
      )}
    </div>
  );
}
